import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  getAdvisorProStatus,
  type AdvisorProState,
  type AdvisorProStatusValue,
  type VerificationStatus,
} from '@/api/advisorPro';

export interface AdvisorProMenuState {
  /** Whether the signed-in advisor currently has Advisor Pro entitlement. */
  isActive: boolean;
  /** True while any onboarding step is still outstanding. */
  hasPendingOnboarding: boolean;
  /** Server-side verification state for the advisor's travel credential. */
  verificationStatus: VerificationStatus;
  /** Billing/subscription lifecycle status from the backend. */
  subscriptionStatus: AdvisorProStatusValue;
}

/** Onboarding is done only once Connect is completed — it is the last step. */
function hasPendingOnboarding(state: AdvisorProState | null): boolean {
  const onboarding = state?.onboarding;
  if (!onboarding) return true;

  return (
    !onboarding.credentials_submitted ||
    !onboarding.services_configured ||
    !onboarding.connect_completed
  );
}

/**
 * Advisor Pro state for the nav "More" menu.
 *
 * Read straight from `GET /api/v2/advisor-pro/status` rather than from the auth
 * context: `user` there is a snapshot cached at login, so it still reports the
 * pre-upgrade state right after checkout.
 *
 * Returns `null` until the answer is known, so callers can avoid flashing the
 * wrong row at an advisor who already paid.
 */
export function useAdvisorProMenuState(enabled: boolean): AdvisorProMenuState | null {
  const [menuState, setMenuState] = useState<AdvisorProMenuState | null>(null);

  useEffect(() => {
    if (!enabled) {
      setMenuState(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    getAdvisorProStatus({ signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const state = response.advisor_pro ?? null;
        setMenuState({
          isActive: state?.is_pro_active === true,
          hasPendingOnboarding: hasPendingOnboarding(state),
          verificationStatus: state?.verification_status ?? 'unsubmitted',
          subscriptionStatus: state?.status ?? 'none',
        });
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        // Fail safe to a visible, non-blocking menu state.
        setMenuState({
          isActive: false,
          hasPendingOnboarding: false,
          verificationStatus: 'unsubmitted',
          subscriptionStatus: 'none',
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [enabled]);

  return menuState;
}
